import { parseArgs } from "../../features/draft/utils/parseArgs";
import { EditInfo } from "../shared/editInfo";
import { ToolResult } from "../shared/toolResult";

// Turning a completed tool call into the cards the chat shows under a message.
//
// In Word these cards carry accept and reject buttons. Here they do not: Excel has no
// revisions model, so `AssistantAdapter.review` is absent and the UI hides the buttons
// (see ChatMessageBubble). The cards are still worth producing -- they are the record
// of what the assistant did to the workbook, in a session where the only way to undo
// it is Excel's own undo. That makes them MORE important than in Word, not less: they
// are the only place a user can see the list of changes to reverse.
//
// So the emphasis is different. Word's cards show a before/after of the text. These
// show the address and what happened to it, because "D501 <- =SUM(D2:D500)" is the
// useful thing and there is no prior value to display (the tool result does not carry
// one, and reading it back would be a round trip per edit).

const truncate = (text: string, limit = 80) =>
  text.length > limit ? text.substring(0, limit - 3) + "..." : text;

const describeValues = (values: unknown): string => {
  if (!Array.isArray(values)) return "values";
  const rows = values.length;
  const columns = Array.isArray(values[0]) ? values[0].length : 1;
  const preview = values
    .slice(0, 2)
    .map((row) => (Array.isArray(row) ? row.slice(0, 4).join(", ") : String(row)))
    .join(" / ");
  return truncate(`${rows}x${columns}: ${preview}`);
};

export function buildEditInfo(
  toolName: string,
  rawArgs: object | string,
  result: ToolResult
): EditInfo | EditInfo[] | null {
  if (!result.success) return null;

  const args = parseArgs<Record<string, any>>(rawArgs);
  const range = typeof args.range === "string" ? args.range : "";

  // A card per change, with `status` left "pending" forever: there is nothing to
  // accept it into, and no bookmarkName either -- which is what tells the shared card
  // not to offer accept/reject. The label is what the user reads.
  //
  // The address comes back off the RESULT in preference to the arguments, because the
  // result's is sheet-qualified and the model's often is not. The jump button on the
  // card feeds this straight to `reveal`, and an unqualified address there selects the
  // right cell on whatever sheet the user happens to be looking at -- which after any
  // other jump is not the sheet the edit landed on.
  const card = (label: string, find: string, replace: string): EditInfo => ({
    segmentId: result.segmentId || range || (typeof args.name === "string" ? args.name : "?"),
    label,
    find,
    replace,
    status: "pending",
  });

  switch (toolName) {
    case "set_values":
      return card("Writing values", "[set values]", describeValues(args.values));

    case "set_formula":
      return card("Setting formula", "[set formula]", truncate(String(args.formula ?? "")));

    case "set_number_format":
      return card("Formatting numbers", "[number format]", String(args.format ?? ""));

    case "format_range": {
      const applied = [
        "bold",
        "italic",
        "underline",
        "fontColour",
        "fillColour",
        "horizontalAlignment",
      ]
        .filter((key) => args[key] !== undefined)
        .map((key) => `${key}=${args[key]}`)
        .join(", ");
      return card("Formatting cells", "[format]", truncate(applied || "formatting"));
    }

    case "insert_rows":
      return card("Inserting rows", "[insert rows]", `blank rows before ${range}`);

    case "insert_columns":
      return card("Inserting columns", "[insert columns]", `blank columns before ${range}`);

    case "delete_rows":
      return card("Deleting rows", `rows ${range}`, "[deleted]");

    case "delete_columns":
      return card("Deleting columns", `columns ${range}`, "[deleted]");

    case "add_sheet":
      return card("Adding sheet", "[new sheet]", String(args.name ?? ""));

    // read_range and select_range change nothing, so they get no card. A card for
    // every read would bury the actual edits.
    default:
      return null;
  }
}
